import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

/* Botão — arquivo do shadcn, reestilizado para a Colmeia.
 *
 * Pílula plena, sem gradiente e sem sombra colorida. Encolhe 1,5% ao apertar,
 * e essa é a ÚNICA deformação do sistema inteiro: um botão que afunda diz que
 * foi apertado melhor do que qualquer mudança de cor, e usar o mesmo truque
 * em outro lugar gastaria o efeito.
 *
 * Dentro de uma célula `campo="mata"` as três variantes de contorno trocam
 * de tinta sozinhas (ver globals.css). Não existe prop de tema: o botão não
 * precisa saber onde está. */

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-pilula border border-transparent font-semibold whitespace-nowrap outline-none transition-[background-color,border-color,color,transform] duration-[var(--rapido)] ease-mola active:scale-[.985] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // A letra é BRANCA (--primary-foreground, ver globals.css): 6,2:1
        // sobre o verde de ação. Era a nata, que puxa creme e fazia o botão
        // principal parecer desabilitado.
        //
        // Existia uma variante "marca" que preenchia com o verde cheio da
        // marca e escrevia em tinta mata por cima. Ela morreu, e o motivo é o
        // que importa: verde claro com letra escura lê como AVISO, não como
        // ação, e era o botão do veredito — a ação mais importante do produto
        // — que estava desenhada assim. Botão cheio agora é um só desenho em
        // todo lugar, inclusive dentro da célula mata (ver globals.css), onde
        // ele ganha um anel do verde da marca para separar do campo escuro.
        default: "bg-primary text-primary-foreground hover:bg-acao-forte",
        destructive:
          "bg-destructive text-destructive-foreground hover:brightness-110 focus-visible:outline-destructive",
        // O CONTRAPONTO DO PRIMÁRIO, não um link com moldura. É a variante do
        // "Voltar" do funil, e ela estava fraca demais para o papel: fundo
        // transparente, borda de 3,2:1 e letra em --tinta-2 somavam um botão
        // que o olho pulava enquanto procurava o par de ações no rodapé.
        // Agora: campo de nata (o mesmo degrau de superfície do primário, para
        // os dois pesarem como objeto), letra em --tinta cheia (13,7:1) e
        // borda em --tinta-3 (4,8:1, bem acima dos 3:1 que a WCAG 1.4.11 pede
        // para contorno de controle). Sobre a barra de ação, que também é
        // nata, quem carrega o botão é a borda; sobre o creme do chão, os dois.
        outline: "border-tinta-3 bg-nata text-tinta hover:bg-areia hover:border-tinta-2",
        secondary: "bg-secondary text-secondary-foreground hover:bg-areia-forte",
        ghost: "text-tinta-2 hover:bg-lavado hover:text-tinta",
        link: "text-primary underline-offset-4 hover:text-acao-forte hover:underline",
      },
      size: {
        default: "h-toque px-5 text-apoio has-[>svg]:px-4",
        xs: "h-6 gap-1 px-2.5 text-micro has-[>svg]:px-2 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1.5 px-3.5 text-nota has-[>svg]:px-3",
        lg: "h-11 px-6 text-corpo has-[>svg]:px-5",
        icon: "size-toque",
        "icon-xs": "size-6 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
